import React from 'react';
import { TimerResult } from 'react-timer-hook';
import { DfcSurface } from '../../../../../Utils/dfcSurface';
import useFitnessVillageCopy from '../../../../../hooks/useFitnessVillageCopy';
import { HoldLabel, HoldStrip, HoldTimer } from './style';

interface CartHoldCountdownProps {
  surface: DfcSurface;
  /**
   * The shared cart hold timer from `CheckoutContext.countdownApi`. It may be missing under
   * jsdom stubs, so the component guards on it as well as `isRunning` before rendering.
   */
  countdownApi?: TimerResult;
}

const pad = (value: number) =>
  value.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });

/**
 * Thin strip under the DFC header, visible only while there is a live cart hold, showing the
 * time remaining before the cart releases its seats. Reuses the app-wide `countdownApi`
 * already spun up in `CheckoutContext` (§Phase 1 deferral, §8.4) so a village booking counts
 * down against the same clock every other theme uses.
 */
const CartHoldCountdown: React.FC<CartHoldCountdownProps> = ({ surface, countdownApi }) => {
  const copy = useFitnessVillageCopy().header;

  if (!countdownApi?.isRunning) return null;
  const { minutes, seconds } = countdownApi;

  return (
    <HoldStrip
      surface={surface}
      role="status"
      aria-live="polite"
      aria-label={copy.cartHoldAria(minutes, seconds)}
      data-testid="cart-hold-strip"
    >
      <HoldLabel surface={surface}>{copy.cartHold}</HoldLabel>
      <HoldTimer surface={surface} aria-hidden="true">
        {pad(minutes)}:{pad(seconds)}
      </HoldTimer>
    </HoldStrip>
  );
};

export default CartHoldCountdown;
